import React from 'react';
import { Box, Typography, Button, Avatar, Chip, useTheme } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { tokens } from "../../../theme";
import { getAuthHeader } from "../../../utils/authToken";
import Axios from "axios";
import dayjs from 'dayjs';

var utc = require('dayjs/plugin/utc');
var timezone = require('dayjs/plugin/timezone');
dayjs.extend(utc);
dayjs.extend(timezone);

const fmt = (d) => (d ? dayjs(d).tz('America/New_York').format('MMM D, YYYY h:mm A') : null);

// hw_status → { text, color, bar } for a finalized round. Boss's colors: green on time, orange
// late (✓ once target reached, even late), red not taken. Undefined status → caller's open-round
// fallback. The personal box spells the status out; the roster row uses the short form.
const HW_STATUS = {
    on_time:         { text: "✓ on time",        short: "✓ on time",   color: "success.main", bar: "success" },
    completed_late:  { text: "✓ completed late", short: "✓ late",      color: "warning.main", bar: "warning" },
    late_incomplete: { text: "· still short",    short: "· short",     color: "warning.main", bar: "warning" },
    not_taken:       { text: "· not taken",      short: "· not taken", color: "error.main",   bar: "error" },
};

// Box for a prep qbank ROUND (Phase 7). One box per homework round. Three shapes:
//  • upcoming (round not released yet): dimmed schedule preview, not expandable.
//  • roster (teacher/admin, item.students present): collapsible accordion to keep the often-
//    long list manageable; expand → per-student rows with their assigned chapters +
//    solved/target. Chapters are picked per student, so the detail is organized by student.
//  • personal (student, item.chapters present): always expanded (students have few boxes) —
//    per-chapter progress bars + "Open in ETT".
// The digest PDF sits behind the same auth as every other API call, so it can't be a plain
// href — the browser wouldn't send the token. Fetch it as a blob and hand the tab an object URL.
const openDigestPdf = async (url) => {
    const res = await Axios.get(`${process.env.REACT_APP_URL}${url}`, {
        headers: { Authorization: getAuthHeader() },
        responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(blobUrl, "_blank", "noopener");
    // Give the new tab time to load before releasing the handle.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

const EttQbankBox = ({ item }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const upcoming = !!item.upcoming;
    const roster = !upcoming && Array.isArray(item.students);
    // Append the subject (e.g. "— Math" / "— R&W") so same-titled boxes for different
    // subjects are distinguishable. Subject omitted when the program-course has no label.
    const title = [item.label || item.chapter_name || "Question-bank homework", item.subject]
        .filter(Boolean)
        .join(" — ");
    const target = item.target_questions || 0;

    // ---- Upcoming: dimmed, non-expandable schedule preview ----
    if (upcoming) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: colors.primary[600], borderRadius: "5px", border: "0.1px solid #a3a3a3", mb: "1.8rem", opacity: 0.6 }}>
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%", borderBottom: "0.1px solid #a3a3a3", py: "0.5rem", px: "1rem", alignItems: "center" }}>
                    <Avatar sx={{ width: 30, height: 30 }}><MenuBookIcon /></Avatar>
                    <Typography variant="h5" sx={{ my: "auto", ml: "1rem" }}>{title}</Typography>
                    <Chip size="small" label="Q-Bank" sx={{ ml: 1 }} />
                    <Chip size="small" variant="outlined" label="Upcoming" sx={{ ml: 0.5 }} />
                </Box>
                <Box sx={{ py: "1rem", px: "1rem" }}>
                    <Typography variant="h7" color={colors.grey[700]}>
                        Opens {fmt(item.release_date)}{item.due_date ? ` · due ${fmt(item.due_date)}` : ""} · {item.num_chapters} chapter{item.num_chapters === 1 ? "" : "s"}{target ? ` × ${target} questions` : ""} (chapters assigned at release)
                    </Typography>
                </Box>
            </Box>
        );
    }

    // ---- Released round (roster or personal) ----
    const overdue = item.due_date && new Date() > new Date(item.due_date);

    let summary;
    if (roster) {
        const doneCount = item.students.filter((s) => s.done).length;
        summary = `${doneCount}/${item.students.length} done`;
    } else {
        const chaptersDone = item.chapters_done ?? (item.chapters || []).filter((c) => c.done).length;
        const chaptersTotal = item.chapters_total ?? (item.chapters || []).length;
        summary = `${chaptersDone}/${chaptersTotal} chapters`;
    }

    // Shared header content (round title + Q-Bank chip + done/chapters summary + due date).
    const header = (
        <Box sx={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", pr: 1 }}>
            <Avatar sx={{ width: 30, height: 30 }}><MenuBookIcon /></Avatar>
            <Typography variant="h5" sx={{ my: "auto", ml: "1rem" }}>{title}</Typography>
            <Chip size="small" label="Q-Bank" sx={{ ml: 1 }} />
            <Box sx={{ ml: "auto", textAlign: "right" }}>
                <Typography variant="h7" color={colors.grey[700]}>{summary}</Typography>
                {item.due_date && (
                    <Typography variant="caption" display="block" color={overdue ? "error" : colors.grey[700]}>
                        Due {fmt(item.due_date)}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    // ---- Personal (student): always expanded — per-chapter progress + "Open in ETT" ----
    if (!roster) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: colors.primary[600], borderRadius: "5px", border: "0.1px solid #a3a3a3", mb: "1.8rem" }}>
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%", borderBottom: "0.1px solid #a3a3a3", py: "0.5rem", px: "1rem", alignItems: "center" }}>
                    {header}
                </Box>
                <Box sx={{ py: "0.75rem", px: "1rem" }}>
                    {(item.chapters || []).length === 0 ? (
                        <Typography variant="caption" color={colors.grey[700]}>No chapters assigned.</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color={colors.grey[700]} sx={{ mb: "0.85rem" }}>
                                Open each chapter and solve problems in ETT until you reach the target for that chapter.
                            </Typography>
                            {item.chapters.map((c) => {
                            const done = c.solved_this_round || 0;
                            const target = c.target_questions || 0;
                            const postDue = Math.max(0, c.post_due || 0);
                            const pct = target ? Math.min(100, Math.round((100 * done) / target)) : 0;
                            const meta = c.hw_status ? HW_STATUS[c.hw_status] : null;
                            const textColor = meta ? meta.color : "text.primary";
                            const barColor = meta ? meta.bar : (pct >= 100 ? "success" : "primary");
                            const statusText = meta ? meta.text : (c.finalized ? (c.on_time ? "✓ on time" : "· by due") : (pct >= 100 ? "✓" : ""));
                            // Two-tone bar: solid = by the due date, hatched = after (capped at target).
                            const byduePct = target ? Math.min(100, Math.round((100 * Math.min(done, target)) / target)) : 0;
                            const latePct = target ? Math.min(100 - byduePct, Math.round((100 * postDue) / target)) : 0;
                            return (
                                <Box key={c.chapter_id} sx={{ mb: "1rem" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                        <Typography variant="h6" sx={{ flexGrow: 1 }}>{c.chapter_name}</Typography>
                                        <Typography variant="caption" color={textColor}>
                                            {done}/{c.target_questions || "—"} {statusText}
                                            {postDue > 0 ? <Box component="span" sx={{ color: "info.main" }}> · +{postDue} after due</Box> : null}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ flexGrow: 1, display: "flex", height: 6, borderRadius: 3, overflow: "hidden", backgroundColor: (t) => t.palette.action.hover }}>
                                            <Box sx={{ width: `${byduePct}%`, bgcolor: `${barColor}.main` }} />
                                            {latePct > 0 ? (
                                                <Box sx={{ width: `${latePct}%`, backgroundImage: (t) => `repeating-linear-gradient(45deg, ${t.palette[barColor].main} 0 4px, rgba(0,0,0,0) 4px 8px)` }} />
                                            ) : null}
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<OpenInNewIcon />}
                                            disabled={!c.ett_deep_link}
                                            onClick={() => c.ett_deep_link && window.open(c.ett_deep_link, "_blank", "noopener")}
                                        >
                                            Open
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}
                        </>
                    )}
                </Box>
            </Box>
        );
    }

    // ---- Roster (teacher/admin): collapsible — expand → per-student detail ----
    return (
        <Accordion
            disableGutters
            sx={{
                backgroundColor: colors.primary[600],
                borderRadius: "5px !important",
                border: "0.1px solid #a3a3a3",
                mb: "1.8rem",
                boxShadow: "none",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderBottom: "0.1px solid #a3a3a3", px: "1rem" }}>
                {header}
            </AccordionSummary>
            <AccordionDetails sx={{ py: "0.75rem", px: "1rem" }}>
                {item.students.length === 0 ? (
                    <Typography variant="caption" color={colors.grey[700]}>No students assigned.</Typography>
                ) : (
                    item.students.map((s) => (
                        <Box key={s.student_id} sx={{ mb: "0.85rem" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                {s.done
                                    ? <CheckCircleIcon fontSize="small" color="success" />
                                    : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: colors.grey[600] }} />}
                                <Typography variant="h6">{s.name}</Typography>
                                <Typography variant="caption" color={colors.grey[700]} sx={{ ml: "auto" }}>
                                    {s.chapters_done}/{s.chapters_total} chapters
                                </Typography>
                            </Box>
                            <Box sx={{ pl: "1.75rem" }}>
                                {(s.chapters || []).map((c) => {
                                    const meta = c.hw_status ? HW_STATUS[c.hw_status] : null;
                                    const color = meta ? meta.color : (c.finalized ? (c.on_time ? "success.main" : "warning.main") : (c.done ? "success.main" : colors.grey[700]));
                                    const statusText = meta ? meta.short : (c.finalized ? (c.on_time ? "✓ on time" : "· by due") : (c.done ? "✓" : ""));
                                    return (
                                        <Box key={c.chapter_id} sx={{ display: "flex", alignItems: "center", gap: 1, py: "0.1rem" }}>
                                            <Typography variant="body2" sx={{ flexGrow: 1 }}>• {c.chapter_name}</Typography>
                                            <Typography variant="caption" color={color}>
                                                {c.solved_this_round}/{c.target_questions || "—"} {statusText}
                                                {c.post_due > 0 ? <Box component="span" sx={{ color: "info.main" }}> (+{c.post_due})</Box> : null}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))
                )}
                {/* Class results. Present only on the roster payload, so a student never receives
                    it — the API attaches it on the teacher/admin branch alone. Deliberately just
                    the link: question stems are LaTeX, and unrendered LaTeX in a summary row is
                    noise. The PDF renders it properly. */}
                {item.teacher_digest && (
                    <Box sx={{ mt: "0.5rem", pt: "0.75rem", borderTop: "0.1px solid #a3a3a3", display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>Class results</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => openDigestPdf(item.teacher_digest.url)}
                        >
                            Open report (PDF)
                        </Button>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default EttQbankBox;
